import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { cartModule } from '../../api/modules/cart.module';
import { userModule } from '../../api/modules/user.module';
import adminModule from '../../api/modules/admin.module';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../Contexts/CartContext';
import { useCartWithListings } from '../../hooks/useCartWithListings';
import { showToast } from '../../utils/toast';
import {
  isPriceUnavailable,
  PRICE_UNAVAILABLE_CHECKOUT_MESSAGE,
} from '../../utils/priceAvailability';
import { track } from '../../analytics/track';
import { EVENTS } from '../../analytics/events';
import { useCheckoutForm } from './hooks/useCheckoutForm';
import { useOrderLock } from './hooks/useOrderLock';
import { buildPlaceOrderPayload } from './utils/buildPlaceOrderPayload';
import { buildRateQuotePayload } from './utils/buildRateQuotePayload';
import { CheckoutView } from './checkout.view';

/**
 * Checkout, the container. Cart re-hydration, the rate quote, the order lock,
 * the place-order mutation, analytics and every redirect.
 *
 * Everything visual lives in `checkout.view.jsx`, which is pure, so the state
 * explorer can mount the real screen without any of this running.
 *
 * NOTHING IN THIS FILE CHANGED ITS BEHAVIOUR. The re-hydration of the minimal
 * `orderItems_*` handoff against fresh listing data, the seller-id recovery and
 * its numeric guard, the stock check, the in-person auto-select, the order
 * lock, the `pendingOrderInfo` write, the Yoco redirect and its 1200ms delay,
 * `CHECKOUT_STARTED` / `MULTI_SELLER_CHECKOUT_STARTED` / `PAYMENT_INITIATED` /
 * `PAYMENT_FAILED`, and every toast string are exactly as they were. The one
 * addition of the design-system conversion is `handoffCleared`, which records
 * the branch that was ALREADY clearing the handoff so the view can say so
 * instead of rendering an empty two-column layout during the 1500ms before the
 * redirect fires.
 *
 * While the admin `sell_in_person` gate is on, every buyer gets the picker and
 * can choose either "Buy in person" or "Ship to me". (Shipping used to be a
 * staff-only override behind a "Coming soon" tile; it is open to all now.)
 */
const Checkout = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { checkoutData, setCheckoutData } = useCart();
  const { cartData, isLoading: listingsLoading, isError: listingsError } = useCartWithListings();

  const [modalOpen, setModalOpen] = useState(false);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [inPersonConfirmOpen, setInPersonConfirmOpen] = useState(false);

  const [excludeDelivery, setExcludeDelivery] = useState(false);
  const [inPersonAcknowledged, setInPersonAcknowledged] = useState(false);
  const [ackError, setAckError] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [billingConfirmed, setBillingConfirmed] = useState(false);
  const [handoffCleared, setHandoffCleared] = useState(false);
  // Task 11: ids the SERVER rejected on `create-order/`'s price guard
  // (`unavailable_listing_ids`, strings) -- kept separately from the
  // client-side `isPriceUnavailable` read below because the two can disagree
  // (e.g. an age-based staleness rule the client does not compute itself).
  // Reset whenever the cart is re-hydrated, see the effect below.
  const [blockedListingIds, setBlockedListingIds] = useState(() => new Set());
  const multiSellerFiredRef = useRef(false);

  const profileQuery = useQuery({
    queryKey: ['sellerProfile'],
    queryFn: async () => {
      const res = await userModule.getSellerProfile();
      if (!res.success) throw new Error(res.message || 'Failed to load profile');
      return res.data?.details || res.data?.data || res.data;
    },
    enabled: isAuthenticated,
  });

  const { data: systemSettings } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: async () => {
      const result = await adminModule.getSystemSettings();
      if (result.success) {
        return result.data;
      }
      return { sell_in_person: false, platform_fee: 5 };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Whenever the admin in-person gate is on, the picker starts on in-person
  // (the existing excludeDelivery=true path: address hidden, shipping 0) and
  // the buyer can switch to "Ship to me".
  useEffect(() => {
    if (systemSettings?.sell_in_person) {
      setExcludeDelivery(true);
    }
  }, [systemSettings?.sell_in_person]);

  const { form, errors, handleChange, validateForm, hasValidAddress } = useCheckoutForm({
    user,
    profileData: profileQuery.data,
    excludeDelivery,
  });

  const orderLock = useOrderLock(user);

  const getOrderItemsKey = () => {
    return user ? `orderItems_${user.id || user._id}` : 'orderItems_guest';
  };

  // `orderItems_<user>` now holds the MINIMAL handoff shape written by the
  // cart page: { sellerId, items: [{ listing_id, quantity }] }. Re-hydrate
  // each minimal item with fresh listing data from useCartWithListings()
  // (freshly-signed images, live price/stock, already stock-clamped) to
  // rebuild the { card_listing, quantity } shape the rest of Checkout
  // (OrderSummary, the stock guard, analytics, buildPlaceOrderPayload)
  // consumes. Never read `item.card_listing.*` directly off a minimal item.
  const getStoreData = () => {
    const orderItemsKey = getOrderItemsKey();
    const orderItems = JSON.parse(localStorage.getItem(orderItemsKey) || '{}');
    let sellerId = orderItems.sellerId;
    const minimalItems = orderItems.items || [];

    if (sellerId && !/^\d+$/.test(sellerId.toString())) {
      console.error(`[Checkout] Invalid sellerId format: "${sellerId}". Expected numeric ID.`);
      sellerId = null;
    }

    if (!sellerId && minimalItems.length > 0) {
      // Try to recover the seller bucket from fresh cart data by matching
      // any minimal item's listing_id against a listing in cartData.
      const targetId = minimalItems[0]?.listing_id;
      const foundSellerId = Object.keys(cartData).find((sid) =>
        (cartData[sid] || []).some((fresh) => fresh.card_listing?.id === targetId)
      );
      if (foundSellerId) {
        sellerId = foundSellerId;
      }
    }

    const freshItems = (sellerId && cartData[sellerId]) || [];

    // Join minimal { listing_id, quantity } with fresh { card_listing, quantity }.
    // Drop items whose listing is no longer available in cartData (pruned
    // upstream by useCartWithListings). Prefer cartData's quantity, it is
    // already clamped to live stock.
    const storeItems = minimalItems
      .map((item) => freshItems.find((fresh) => fresh.card_listing?.id === item.listing_id))
      .filter(Boolean);

    return { sellerId, storeItems };
  };

  // A row is blocked either because `price_stale` on the FRESH listing data
  // says so (read live off `card_listing` every render, never persisted --
  // see `useCartListings`/`ListingsByIdsView`), or because the server named
  // this exact listing in a prior `create-order/` 400. THE ID-TYPE TRAP: the
  // server sends `unavailable_listing_ids` as STRINGS (`str(l.id)` in
  // `apps/api/apps/orders/views.py`), while `card_listing.id` here comes off
  // `ListingsByIdsView`, which parses ids as INTEGERS and serializes them as
  // JSON numbers. `Set.has`/`Array.includes` are both strict-equality, so
  // comparing without coercion silently matches nothing. Coerce both sides.
  const isCheckoutItemBlocked = (item) =>
    isPriceUnavailable(item?.card_listing) || blockedListingIds.has(String(item?.card_listing?.id));

  useEffect(() => {
    // Wait for auth to hydrate. While `user` is null `getOrderItemsKey()`
    // returns the guest key, which is always absent for a signed-in buyer and
    // would bounce them straight off their own checkout.
    if (!user) return;

    // A fresh listings fetch supersedes any earlier server-side rejection --
    // the buyer may have gone back to the cart and removed the offending row,
    // or the price may since have refreshed. `isCheckoutItemBlocked` still
    // catches a listing that is STILL stale, from the live `price_stale` on
    // the fresh data itself; this only clears the OTHER half, the
    // `unavailable_listing_ids` a prior 400 named.
    setBlockedListingIds(new Set());

    const orderItemsKey = getOrderItemsKey();
    const storedData = localStorage.getItem(orderItemsKey);

    // No handoff at all: /checkout was opened directly instead of through the
    // cart page, which is the only thing that writes it. Send them to the cart
    // rather than rendering a R0.00 order with a live Place Order button.
    // `handoffCleared` covers the frame before the redirect commits.
    if (!storedData) {
      setCheckoutData({});
      setHandoffCleared(true);
      navigate('/cart', { replace: true });
      return;
    }

    // Wait for fresh listing data before deciding whether the handoff is
    // still valid, because cartData is empty until the listings fetch resolves,
    // which would otherwise look like "items no longer in cart".
    if (listingsLoading) return;

    const { sellerId, storeItems } = getStoreData();

    if (sellerId && storeItems.length > 0) {
      setCheckoutData({ sellerId, items: storeItems });
      setHandoffCleared(false);
    } else {
      localStorage.removeItem(orderItemsKey);
      setCheckoutData({});
      setHandoffCleared(true);
      showToast.error('Your cart has been updated. Please select items again.');
      setTimeout(() => navigate('/cart'), 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData, listingsLoading, user]);

  const confirmBillingMutation = useMutation({
    mutationFn: cartModule.getRate,
    onSuccess: (result) => {
      if (result.success) {
        setShippingCost(result.data.rate || 0);
        setBillingConfirmed(true);
        showToast.success('Billing information confirmed');
      } else {
        showToast.error(result.message);
      }
    },
    onError: () => {
      showToast.error(
        'Unexpected error getting shipping rate. Please provide valid billing details.'
      );
    },
  });

  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    if (listingsLoading) {
      showToast.error('Please wait while we refresh your cart.');
      return;
    }

    if (listingsError) {
      showToast.error("Couldn't load your cart. Please refresh and try again.");
      return;
    }

    const { sellerId, storeItems } = getStoreData();

    if (!storeItems.length) {
      showToast.error('Cart is empty');
      return;
    }

    const finalSellerId = sellerId || storeItems[0]?.card_listing?.seller?.id;
    if (!finalSellerId || !/^\d+$/.test(finalSellerId.toString())) {
      console.error('[Checkout] Invalid seller ID:', finalSellerId);
      showToast.error('Invalid seller information. Please refresh your cart and try again.');
      return;
    }

    if (!excludeDelivery && !hasValidAddress) {
      showToast.error(
        'Please select a saved address or enter a complete valid address before confirming.'
      );
      return;
    }

    confirmBillingMutation.mutate(buildRateQuotePayload({ form, storeItems, finalSellerId }));
  };

  const placeOrderMutation = useMutation({
    mutationKey: ['place-order'],
    // `payload` is the request body; `meta` stays client-side (used below to
    // build pendingOrderInfo) and must never go over the wire.
    mutationFn: async ({ payload }) => {
      orderLock.acquire();

      try {
        const result = await cartModule.placeOrder(payload);
        return result;
      } catch (error) {
        orderLock.release();
        throw error;
      }
    },
    onSuccess: (res, { meta: { sellerId, storeItems, isInPerson } }) => {
      if (res.success) {
        const orderInfo = {
          sellerId,
          storeItems,
          isInPerson: isInPerson || false,
          orderId: res.data?.order_id || res.data?.id,
          timestamp: new Date().getTime(),
          paymentConfirmed: false,
        };
        localStorage.setItem('pendingOrderInfo', JSON.stringify(orderInfo));

        if (res.data?.redirect_url) {
          track(EVENTS.PAYMENT_INITIATED, {
            order_id: res.data?.order_id || res.data?.id,
            amount: res.data?.amount || res.data?.total,
          });
          setTimeout(() => {
            window.location.href = res.data.redirect_url;
          }, 1200);
        } else {
          orderInfo.paymentConfirmed = true;
          localStorage.setItem('pendingOrderInfo', JSON.stringify(orderInfo));
          setPaymentStatus('success');
          setResultModalOpen(true);
        }
      } else {
        orderLock.release();
        setModalOpen(false);

        // Task 11: the price-guard rejection, not a generic failure --
        // `unavailable_listing_ids` names the exact rows, so mark exactly
        // those rather than the opaque "payment failed" screen below. Nothing
        // was charged (the guard runs before any payment step), so there is
        // nothing to explain via a result modal; the buyer stays on the page
        // and sees which rows are blocked.
        //
        // THE ID-TYPE TRAP: these arrive as STRINGS (`str(l.id)` server-side)
        // -- coerce with String() before they are compared against anything,
        // see `isCheckoutItemBlocked` above.
        const unavailableIds = res.error?.unavailable_listing_ids;
        if (Array.isArray(unavailableIds) && unavailableIds.length > 0) {
          setBlockedListingIds(new Set(unavailableIds.map(String)));
          track(EVENTS.PAYMENT_FAILED, { failure_reason: 'price_unavailable' });
          showToast.error(res.message || PRICE_UNAVAILABLE_CHECKOUT_MESSAGE);
          return;
        }

        setPaymentStatus('failed');
        setResultModalOpen(true);
        track(EVENTS.PAYMENT_FAILED, { failure_reason: res.message || 'place_order_failed' });
        showToast.error(res.message || 'Something went wrong');
      }
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
      orderLock.release();
      setModalOpen(false);
      setPaymentStatus('failed');
      setResultModalOpen(true);
      track(EVENTS.PAYMENT_FAILED, { failure_reason: error.message || 'network_error' });
      showToast.error(error.message || 'Order failed. Please try again.');
    },
  });

  const handlePlaceOrder = async () => {
    if (!isAuthenticated) {
      showToast.error('Please login to add items to your cart.');
      navigate('/login');
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (listingsLoading) {
      showToast.error('Please wait while we refresh your cart.');
      return;
    }

    if (listingsError) {
      showToast.error("Couldn't load your cart. Please refresh and try again.");
      return;
    }

    const { sellerId, storeItems } = getStoreData();

    if (!storeItems.length) {
      showToast.error('Cart is empty');
      return;
    }

    const finalSellerId = sellerId || storeItems[0]?.card_listing?.seller?.id;
    if (!finalSellerId || !/^\d+$/.test(finalSellerId.toString())) {
      console.error('[Checkout] Invalid seller ID:', finalSellerId);
      showToast.error('Invalid seller information. Please refresh your cart and try again.');
      return;
    }

    const hasInsufficientStock = storeItems.some((item) => {
      const requestedQty = item.quantity;
      const availableStock = item.card_listing.quantity;
      if (requestedQty > availableStock) {
        showToast.error(
          `${item.card_listing.card?.name || 'Item'} only has ${availableStock} in stock, but you're trying to purchase ${requestedQty}.`
        );
        return true;
      }
      return false;
    });

    if (hasInsufficientStock) {
      console.error('[Checkout] Insufficient stock for one or more items');
      return;
    }

    // The AFFORDANCE, not the guarantee: `POST /api/orders/create-order/`
    // still refuses these server-side regardless of this check (see the
    // mutation's `unavailable_listing_ids` handling above). This only saves a
    // round trip the server was always going to reject.
    const hasUnavailablePrice = storeItems.some((item) => isCheckoutItemBlocked(item));
    if (hasUnavailablePrice) {
      console.error('[Checkout] Price unavailable for one or more items');
      showToast.error(PRICE_UNAVAILABLE_CHECKOUT_MESSAGE);
      return;
    }

    const payload = buildPlaceOrderPayload({
      form,
      storeItems,
      finalSellerId,
      excludeDelivery,
    });

    const cartKeyCO = user ? `cart_${user.id || user._id}` : 'cart_guest';
    const cartCO = JSON.parse(localStorage.getItem(cartKeyCO) || '{}');
    const sellerCountCO = Object.keys(cartCO).length || 1;
    const cartValueCO = storeItems.reduce(
      (s, it) => s + (it.card_listing?.price || 0) * (it.quantity || 0),
      0
    );
    const checkoutPayload = {
      cart_id: cartKeyCO,
      cart_value: cartValueCO,
      seller_count: sellerCountCO,
      seller_id: finalSellerId,
      currency: form.currency,
      country: form.country,
      is_in_person: excludeDelivery,
      items_count: storeItems.length,
    };
    track(EVENTS.CHECKOUT_STARTED, checkoutPayload);
    if (sellerCountCO >= 2 && !multiSellerFiredRef.current) {
      multiSellerFiredRef.current = true;
      track(EVENTS.MULTI_SELLER_CHECKOUT_STARTED, {
        seller_count: sellerCountCO,
        item_count: storeItems.length,
        cart_value: cartValueCO,
      });
    }

    placeOrderMutation.mutate({
      payload,
      meta: {
        sellerId: finalSellerId,
        storeItems,
        isInPerson: excludeDelivery,
      },
    });
  };

  // On the in-person path the button stays enabled even before the buyer ticks
  // the confirmation. Clicking Place Order without it is blocked at click time
  // and highlights the checkbox (see the button handler + `ackError`). The
  // shipping path still requires a confirmed, valid address.
  // Task 11: never enabled while any rendered row is blocked, whether that
  // came from the live `price_stale` on fresh listing data or from a prior
  // server rejection (`blockedListingIds`). See `isCheckoutItemBlocked`.
  const hasBlockedItems = (checkoutData?.items || []).some((item) => isCheckoutItemBlocked(item));

  const placeOrderEnabled =
    !listingsLoading &&
    !listingsError &&
    !hasBlockedItems &&
    (excludeDelivery || (billingConfirmed && hasValidAddress));

  useEffect(() => {
    if (excludeDelivery) {
      setShippingCost(0);
      setBillingConfirmed(false);
    }
  }, [excludeDelivery]);

  useEffect(() => {
    if (billingConfirmed && !excludeDelivery) {
      setBillingConfirmed(false);
      setShippingCost(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.billing_address_line1,
    form.billing_address_line2,
    form.city,
    form.state,
    form.postal_code,
  ]);

  /* DISMISS IS DISMISS. This used to re-run the login check and the cart check
     first and only reach `setModalOpen(false)` if both passed, which is a
     proceed-time guard wired to the cancel path: any state that failed either
     check left Cancel, the scrim, Escape and the desktop close button all
     doing nothing but firing a toast, with the dialog still up. Nothing here
     needs guarding -- closing a confirmation neither spends money nor reads the
     cart. Those two checks belong to `handlePlaceOrder`, which still runs them
     before the order goes out. */
  const handleProceedModalClose = () => {
    setModalOpen(false);
  };

  const handleResultModalClose = () => {
    setResultModalOpen(false);
    if (paymentStatus === 'success') navigate('/payment-successfull?confirmed=true');
    if (paymentStatus === 'failed') navigate('/payment-failed');
  };

  // The Place Order click gate, unchanged: auth, validation, a non-empty
  // bucket, then either the in-person acknowledgement or the payment modal.
  const handlePlaceOrderClick = () => {
    if (!isAuthenticated) {
      showToast.error('Please login to add items to your cart.');
      navigate('/login');
      return;
    }

    if (!validateForm()) {
      if (!excludeDelivery && !hasValidAddress) {
        showToast.error(
          'Please select a saved address or enter a complete valid address before proceeding.'
        );
      }
      return;
    }

    const { storeItems } = getStoreData();

    if (!storeItems.length) {
      showToast.error('Cart is empty');
      return;
    }

    if (excludeDelivery) {
      if (!inPersonAcknowledged) {
        setAckError(true);
        showToast.error('Please confirm the seller is with you before placing your order.');
        return;
      }
      setInPersonConfirmOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  return (
    <CheckoutView
      loading={listingsLoading}
      cartUnavailable={handoffCleared}
      form={form}
      errors={errors}
      handleChange={handleChange}
      showFulfillment={Boolean(systemSettings?.sell_in_person)}
      canSelectShipping
      fulfillmentSelected={excludeDelivery ? 'in_person' : 'ship'}
      onFulfillmentSelect={(method) => {
        setExcludeDelivery(method === 'in_person');
        setAckError(false);
      }}
      excludeDelivery={excludeDelivery}
      inPersonAcknowledged={inPersonAcknowledged}
      onAcknowledgeChange={(next) => {
        setInPersonAcknowledged(next);
        if (next) setAckError(false);
      }}
      ackError={ackError}
      checkoutData={checkoutData}
      blockedListingIds={blockedListingIds}
      shippingCost={shippingCost}
      billingConfirmed={billingConfirmed}
      confirmPending={confirmBillingMutation.isPending}
      hasValidAddress={hasValidAddress}
      listingsError={listingsError}
      onConfirmBilling={handleConfirm}
      placeOrderEnabled={placeOrderEnabled}
      onPlaceOrder={handlePlaceOrderClick}
      proceedOpen={modalOpen}
      onProceedClose={handleProceedModalClose}
      onProceed={handlePlaceOrder}
      placingOrder={placeOrderMutation.isPending}
      inPersonConfirmOpen={inPersonConfirmOpen}
      onInPersonCancel={() => setInPersonConfirmOpen(false)}
      onInPersonContinue={() => {
        setInPersonConfirmOpen(false);
        setModalOpen(true);
      }}
      resultOpen={resultModalOpen}
      paymentStatus={paymentStatus}
      onResultClose={handleResultModalClose}
      onPaymentSuccess={() => navigate('/payment-successfull?confirmed=true')}
      onPaymentFailure={() => navigate('/payment-failed')}
    />
  );
};

export default Checkout;
